import { Head, Link, useForm } from "@inertiajs/react";
import { ArrowLeft, Save } from "lucide-react";

import { CategorySubcategorySelector } from "@/components/category-subcategory-selector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem, Product } from "@/types";

interface ProductNewProps {
  product: Product;
}

export default function ProductNew({ product }: ProductNewProps) {
  const routes = useOrganizationRoutes();
  const productsHref = routes.products.index();
  const { data, setData, post, processing, errors } = useForm<{
    normalized_name: string;
    description: string;
    internal_code: string;
    category_id: string;
    subcategory_id: string;
    // Rails validation errors come as association names, not column names
    category?: string;
    subcategory?: string;
  }>({
    normalized_name: product.normalized_name ?? "",
    description: product.description ?? "",
    internal_code: product.internal_code ?? "",
    category_id: product.category_id?.toString() ?? "",
    subcategory_id: product.subcategory_id?.toString() ?? "",
  });

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Productos",
      href: productsHref,
    },
    {
      title: "Nuevo Producto",
      href: "#",
    },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(productsHref);
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Nuevo Producto" />
      <div className="flex h-full flex-1 flex-col gap-6 p-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={productsHref}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Nuevo Producto</h1>
            <p className="text-muted-foreground">
              Crea un nuevo producto en el catálogo
            </p>
          </div>
        </div>

        {/* Form */}
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Información del Producto</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="normalized_name">Nombre *</Label>
                <Input
                  id="normalized_name"
                  value={data.normalized_name}
                  onChange={(e) => setData("normalized_name", e.target.value)}
                  placeholder="Ingrese el nombre del producto"
                  disabled={processing}
                />
                {errors.normalized_name && (
                  <p className="text-sm text-red-500">
                    {errors.normalized_name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="internal_code">Código Interno</Label>
                <Input
                  id="internal_code"
                  value={data.internal_code}
                  onChange={(e) => setData("internal_code", e.target.value)}
                  placeholder="Código interno del producto"
                  disabled={processing}
                />
                {errors.internal_code && (
                  <p className="text-sm text-red-500">{errors.internal_code}</p>
                )}
              </div>

              <CategorySubcategorySelector
                organizationSlug={routes.organizationSlug}
                categoryId={data.category_id}
                subcategoryId={data.subcategory_id}
                onCategoryChange={(id) =>
                  setData("category_id", id?.toString() ?? "")
                }
                onSubcategoryChange={(id) =>
                  setData("subcategory_id", id?.toString() ?? "")
                }
                categoryError={errors.category_id ?? errors.category}
                subcategoryError={errors.subcategory_id ?? errors.subcategory}
                disabled={processing}
              />

              <div className="space-y-2">
                <Label htmlFor="description">Descripción (opcional)</Label>
                <TextareaWithCounter
                  id="description"
                  value={data.description}
                  onChange={(e) => setData("description", e.target.value)}
                  placeholder="Descripción del producto"
                  disabled={processing}
                  rows={4}
                  maxLength={500}
                  showCharacterCount={true}
                />
                {errors.description && (
                  <p className="text-sm text-red-500">{errors.description}</p>
                )}
              </div>

              <div className="flex items-center gap-4">
                <Button type="submit" disabled={processing}>
                  <Save className="h-4 w-4 mr-2" />
                  {processing ? "Creando..." : "Crear Producto"}
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link href={productsHref}>Cancelar</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
